import {
  Component,
  OnDestroy,
  inject,
  signal,
  effect,
  computed,
} from '@angular/core'
import { toObservable } from '@angular/core/rxjs-interop'
import { KENDO_FILTER, FilterExpression } from '@progress/kendo-angular-filter'
import {
  CompositeFilterDescriptor,
  FilterDescriptor,
} from '@progress/kendo-data-query'
import { Subscription } from 'rxjs'
import { debounceTime } from 'rxjs/operators'

import {
  supportedTools,
  SupportedTools,
} from '@cyborg-monorepo/hunter/matchboi'
import {
  TypographyComponent,
  MonacoComponent,
  ButtonComponent,
  LinkComponent,
  SnackbarService,
  DialogService,
} from '@hyperkit'

import { NavigationService, TuningAPIService } from '@hunt/services'

import { supportedPivot } from '../../../../../../../helpers/utils'
import { HuntTemplateService } from '../../../../../../../modules/shared/services'
import {
  TrackingEventEnum,
  TrackingService,
} from '../../../../../../../tracking/tracking.service'
import { EditorComponent } from '../../../../../details/components/hyperkit'
import { CaptureDataDialogComponent } from '../../../../../dialogs/capture-data-dialog/capture-data-dialog.component'
import { QueryEditorService } from '../../../../../services/editor/query-edit.service'
import { HuntViewService } from '../../../../../services/reader/hunt-view.service'
import { PackageViewService } from '../../../../../services/reader/package-view.service'
import { QueryViewService } from '../../../../../services/reader/query-view.service'
import { SelectedQueryPath } from '../../../../../services/utils/navigation.utils'
import { getToolSearchUrl } from '../../../../../services/utils/search-url'

import { OriginalQueryComponent } from './original-query-dialog/original-query-dialog.component'
import { RevertConfirmationComponent } from './revert-confirmation-dialog/revert-confirmation-dialog.component'
@Component({
  standalone: true,
  selector: 'query-selected-overview-tab',
  imports: [
    TypographyComponent,
    MonacoComponent,
    ButtonComponent,
    LinkComponent,
    EditorComponent,
    KENDO_FILTER,
  ],
  templateUrl: './overview-tab.component.html',
  styleUrls: ['./overview-tab.component.scss'],
})
export class OverviewTabComponent implements OnDestroy {
  public readonly queryView = inject(QueryViewService)
  public readonly queryEditor = inject(QueryEditorService)
  public readonly huntView = inject(HuntViewService)
  public readonly pkgView = inject(PackageViewService)
  public readonly templateSvc = inject(HuntTemplateService)
  public readonly snack = inject(SnackbarService)
  public readonly dialog = inject(DialogService)
  readonly toolType = inject(QueryViewService).selectedToolType
  public readonly tuningAPIService = inject(TuningAPIService)
  public nav = inject(NavigationService)
  public readonly trackingService = inject(TrackingService)
  readonly supportedPivot = supportedPivot(this.toolType())
  readonly huntCategory = inject(PackageViewService).huntCategory

  public tools: SupportedTools[] = [...supportedTools]
  tool = signal<SupportedTools>(this.queryView.selectedToolType())

  // Build the field list for the current tool (preserve value on tool change)
  filters = signal<FilterExpression[]>([])
  filterValue = signal<CompositeFilterDescriptor>({ logic: 'and', filters: [] })

  // Render outputs
  astJSON = signal<string>('{}')
  emitted = signal<string>('')

  /** the currently‐selected query/pivot node from the draft */
  readonly selectedQuery = this.queryView.selectedQuery

  /** true if this is a pivot query (pivotPath non-empty) */
  readonly isPivot = this.queryView.isPivotQuery

  /** bound to the Monaco editor */
  readonly rawQuery = signal<string>('')
  readonly formattedNotes = signal<string>('')

  /** sync subscription for auto-saving */
  private syncSub: Subscription = new Subscription()

  /** track the path the sync sub was created for */
  private lastSyncPath: SelectedQueryPath | null = null

  readonly rawQuery$ = toObservable(this.rawQuery)
  readonly formattedNotes$ = toObservable(this.formattedNotes)
  /** expose util in template */
  public readonly getToolSearchUrl = getToolSearchUrl
  readonly selectedQueryId = computed(() => this.selectedQuery()?.id)
  readonly selectedQueryPath = this.queryView.selectedQueryPath

  constructor() {
    // initialize editor values when selected query id changes
    let previousId: string | undefined

    effect(() => {
      const id = this.selectedQueryId()
      const q = this.selectedQuery()
      if (!id || !q || previousId === id) return

      // flush any in-progress edits for the previous query
      if (this.lastSyncPath) {
        this.queryEditor.updateQueryContent(
          { query: this.rawQuery(), notes: this.formattedNotes() },
          this.lastSyncPath,
        )
      }

      previousId = id

      this.rawQuery.set(q.query ?? '')
      this.formattedNotes.set(q.notes ?? '')

      this.syncSub?.unsubscribe()
      const path = this.selectedQueryPath()
      if (path) {
        this.lastSyncPath = path
        this.syncSub = this.queryEditor.sync(
          this.rawQuery$.pipe(debounceTime(500)),
          this.formattedNotes$.pipe(debounceTime(500)),
          path,
        )
      } else {
        this.lastSyncPath = null
      }
    })
  }

  toggleTuning(): void {
    this.nav.mergeQueryParams({ showTuning: !this.nav.showTuning() })
  }

  ngOnDestroy(): void {
    if (this.lastSyncPath) {
      this.queryEditor.updateQueryContent(
        { query: this.rawQuery(), notes: this.formattedNotes() },
        this.lastSyncPath,
      )
    }
    this.syncSub.unsubscribe()
  }
  copyQuery(): void {
    const q = this.selectedQuery()?.query ?? ''
    navigator.clipboard.writeText(q)
    this.snack.show('Query copied')
  }

  saveToTemplate(): void {
    const q = this.selectedQuery()
    if (!q) return

    this.templateSvc
      .saveQueryToTemplate(this.huntView.huntTemplateId(), {
        savedContent: q,
        tool: this.queryView.selectedTool()!,
        environment: this.queryView.selectedEnvironment()!,
        huntPackageName: this.pkgView.title(),
        huntPackUUID: this.pkgView.UUID(),
      })
      .subscribe(() => {
        this.snack.show('Query saved to template')
      })
  }

  viewOriginal(): void {
    this.dialog.open(OriginalQueryComponent, {
      panelClass: 'hyperkit-component',
    })
  }

  revertToOriginal(): void {
    const dialogRef = this.dialog.open<boolean>(RevertConfirmationComponent, {
      panelClass: 'hyperkit-component',
    })

    dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        const orig = this.queryView.originalQuery()
        if (!orig) return
        this.queryEditor.updateQueryContent({ query: orig })
        this.rawQuery.set(orig)

        this.snack.show('Query reverted to original')
      }
    })
  }

  trackOpenInTool(): void {
    this.trackingService.trackEvent(
      TrackingEventEnum.RESEARCH_OPEN_IN_TOOL_CLICKED,
    )
  }

  /** open the “capture pivot” dialog */
  onPivot(): void {
    this.nav.mergeQueryParams({ showCaptureDialog: true, step: 'select' })
    this.dialog.open(CaptureDataDialogComponent, {
      panelClass: 'hyperkit-component',
    })
  }
}
